// How a hero photo is framed and darkened. One definition, read by both
// templates' heroes and by the editor, so a photo set up under Classic
// lands the same way after a switch to Trailhead.
//
// `focus` is the point of the photo that must stay on screen, as
// percentages of its width and height. Used as object-position (and
// background-position) it places that point at the same fraction of
// the frame, which keeps it inside the frame at every shape a screen
// can crop the photo to. Centre is the default, and what every hero
// saved before this option did.
//
// `overlay` is extra darkening on top of the template's own scrim, 0
// to MAX_OVERLAY percent. 0 is the default, so an existing hero looks
// exactly as it did.
//
// No imports, so the harness can run it in node.

export const MAX_OVERLAY = 80;

function clampPct(v, fallback, max = 100) {
    const n = Number(v);
    if (!Number.isFinite(n)) return fallback;
    return Math.min(max, Math.max(0, Math.round(n)));
}

export function heroFocus(config) {
    const f = config?.focus || {};
    return { x: clampPct(f.x, 50), y: clampPct(f.y, 50) };
}

export function heroFocusPosition(config) {
    const { x, y } = heroFocus(config);
    return `${x}% ${y}%`;
}

export function heroOverlay(config) {
    return clampPct(config?.overlay, 0, MAX_OVERLAY);
}

/** The darkening layer's style, or null when there is none to draw. */
export function heroOverlayStyle(config) {
    const pct = heroOverlay(config);
    return pct > 0 ? { backgroundColor: `rgba(0, 0, 0, ${pct / 100})` } : null;
}

/**
 * Whether a hero has anything to say: words, a button or a photo. Every
 * template's hero asks it, together with whatever it draws from the
 * organization's data (a status pill, tiles), and draws nothing at all
 * for a visitor when the answer is no — a band with nothing in it reads
 * as a page that failed to load. The editor canvas still draws it, so
 * there is something to click (Mus, 2026-10-11).
 */
export function heroHasContent(config) {
    const t = (v) => String(v ?? '').trim() !== '';
    return t(config?.title) || t(config?.title_accent) || t(config?.eyebrow) || t(config?.subtitle)
        || t(config?.image) || t(config?.cta_primary?.label) || t(config?.cta_secondary?.label);
}
